import type { AdGraphVisualization } from '@terra/shared-dto-chat-types';
import type { ParsedAdGraphOutput } from './parse-ad-graph-output.util';

const MAX_PAYLOAD_PARSE_DEPTH = 8;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function readOptionalString(value: unknown): string | undefined {
  return typeof value === 'string' && value.length > 0 ? value : undefined;
}

function safeJsonParse(value: string): unknown {
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
}

function toAdGraphVisualization(
  record: Record<string, unknown>,
): AdGraphVisualization | null {
  if (record.type !== 'ad_graph_visualization') {
    return null;
  }

  const title = readOptionalString(record.title);
  if (!title || !isRecord(record.graph)) {
    return null;
  }

  const graphRecord = record.graph;
  if (!Array.isArray(graphRecord.nodes) || !Array.isArray(graphRecord.edges)) {
    return null;
  }

  const nodes = graphRecord.nodes.flatMap((node) => {
    if (!isRecord(node)) {
      return [];
    }
    const id = readOptionalString(node.id);
    const kind = readOptionalString(node.kind);
    const label = readOptionalString(node.label);
    if (!id || !kind || !label) {
      return [];
    }
    return [
      {
        id,
        kind,
        label,
        isCrownJewel:
          typeof node.isCrownJewel === 'boolean'
            ? node.isCrownJewel
            : undefined,
        isOwned: typeof node.isOwned === 'boolean' ? node.isOwned : undefined,
      },
    ];
  });

  const edges = graphRecord.edges.flatMap((edge) => {
    if (!isRecord(edge)) {
      return [];
    }
    const sourceId = readOptionalString(edge.sourceId);
    const targetId = readOptionalString(edge.targetId);
    const relationshipType = readOptionalString(edge.relationshipType);
    if (!sourceId || !targetId || !relationshipType) {
      return [];
    }
    return [
      {
        sourceId,
        targetId,
        relationshipType,
        isAbusable:
          typeof edge.isAbusable === 'boolean' ? edge.isAbusable : undefined,
      },
    ];
  });

  if (nodes.length === 0 || edges.length === 0) {
    return null;
  }

  return {
    type: 'ad_graph_visualization',
    title,
    description: readOptionalString(record.description),
    graph: {
      nodes,
      edges,
      truncated: graphRecord.truncated === true,
      totalCount:
        typeof graphRecord.totalCount === 'number'
          ? graphRecord.totalCount
          : undefined,
    },
  };
}

function readPayload(value: unknown, depth = 0): AdGraphVisualization | null {
  if (depth > MAX_PAYLOAD_PARSE_DEPTH) {
    return null;
  }

  const parsed = typeof value === 'string' ? safeJsonParse(value) : value;
  if (!isRecord(parsed)) {
    return null;
  }

  const direct = toAdGraphVisualization(parsed);
  if (direct) {
    return direct;
  }

  const nested = parsed.output ?? parsed.result ?? parsed.content;
  if (nested && nested !== parsed) {
    return readPayload(nested, depth + 1);
  }

  return null;
}

export function readAdGraphVisualization(
  output: unknown,
  input: unknown,
): AdGraphVisualization | null {
  return readPayload(output) ?? readPayload(input);
}

export function adGraphVisualizationToParsedOutput(
  visualization: AdGraphVisualization,
): ParsedAdGraphOutput {
  return {
    nodeCount: visualization.graph.nodes.length,
    totalCount: visualization.graph.totalCount,
    truncated: visualization.graph.truncated === true,
    nodes: visualization.graph.nodes.map((node) => ({
      id: node.id,
      kind: node.kind,
      label: node.label,
      isCrownJewel: node.isCrownJewel,
      isOwned: node.isOwned,
    })),
    edgeCount: visualization.graph.edges.length,
    edges: visualization.graph.edges.map((edge) => ({
      sourceId: edge.sourceId,
      targetId: edge.targetId,
      relationshipType: edge.relationshipType,
      isAbusable: edge.isAbusable,
    })),
  };
}
